/**
 * Razorpay Magic Checkout runs outside the theme's dialog lifecycle. When it
 * launches from an open cart drawer, both own a scroll lock, and the theme's
 * `scroll-lock` attribute is left stranded on <body> after both close.
 *
 * Two signals are used:
 *   - `razorpay-magic-scroll` flickers on <body> when checkout activates ->
 *     close the drawer through the theme's own close path.
 *   - `.razorpay-container` gains/loses `razorpay-container-visible` ->
 *     the reliable open/close signal. On close, strip any orphaned lock.
 */
(() => {
  // --- Verify LOCK_ATTR against your theme if this doesn't work -------------
  const LOCK_ATTR = 'scroll-lock';
  const RZP_CLASS = 'razorpay-magic-scroll';
  const RZP_CONTAINER = '.razorpay-container';
  const RZP_VISIBLE = 'razorpay-container-visible';
  // -------------------------------------------------------------------------

  const GRACE_MS = 400; // let Razorpay finish its own teardown first

  const razorpayIsUp = () => {
    const container = document.querySelector(RZP_CONTAINER);
    return Boolean(container && container.classList.contains(RZP_VISIBLE));
  };

  const releaseIfOrphaned = () => {
    const locked = [document.body, document.documentElement].filter((el) => el.hasAttribute(LOCK_ATTR));
    if (!locked.length) return;

    // Something legitimately owns the lock — not ours to touch.
    if (razorpayIsUp() || document.querySelector('dialog[open]')) return;

    for (const el of locked) el.removeAttribute(LOCK_ATTR);
  };

  const closeOpenDrawers = () => {
    for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
      if (typeof drawer.close === 'function') drawer.close();
    }
  };

  /** Watch a `.razorpay-container` for its visible class being toggled off. */
  const watchContainer = (container) => {
    if (container.dataset.scrollLockWatched) return;
    container.dataset.scrollLockWatched = 'true';

    let wasVisible = container.classList.contains(RZP_VISIBLE);

    new MutationObserver(() => {
      const isVisible = container.classList.contains(RZP_VISIBLE);

      if (isVisible && !wasVisible) {
        // Razorpay just opened. Get the drawer out of the way so the theme
        // releases its own lock instead of leaving it for Razorpay to restore.
        closeOpenDrawers();
      }

      if (!isVisible && wasVisible) {
        // Razorpay just closed.
        setTimeout(releaseIfOrphaned, GRACE_MS);
      }

      wasVisible = isVisible;
    }).observe(container, { attributes: true, attributeFilter: ['class'] });
  };

  const start = () => {
    // The container may not exist until checkout is first invoked.
    const existing = document.querySelector(RZP_CONTAINER);
    if (existing) watchContainer(existing);

    new MutationObserver(() => {
      const container = document.querySelector(RZP_CONTAINER);
      if (container) watchContainer(container);
    }).observe(document.body, { childList: true });

    // Belt and braces: the body class flicker fires at launch, before the
    // container may exist. Close the drawer on its rising edge too.
    let classWasPresent = document.body.classList.contains(RZP_CLASS);
    new MutationObserver(() => {
      const present = document.body.classList.contains(RZP_CLASS);
      if (present && !classWasPresent) closeOpenDrawers();
      classWasPresent = present;
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Back-button dismiss and bfcache restore are the other stranding paths.
    window.addEventListener('popstate', () => setTimeout(releaseIfOrphaned, GRACE_MS));
    window.addEventListener('pageshow', releaseIfOrphaned);
  };

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }
})();